import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {
    fetchAppData,
    setNotifications,
    setMessage
} from "./system/app-slice";
import {useTranslate} from "./system/useTranslate";
import {Toast} from "primereact/toast";
import {Message} from "primereact/message";
import Auth from "./auth";
import {matchPath, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import PageNotFound from "./system/page-not-found";
import Sidebar from "./system/components/sidebar";
import Page from "./system/components/page";
import {
    getAuthToken,
    setApiErrorHandler,
    setApiUnauthorizedHandler,
    setHeaderToken,
    setWorkspaceMutationGuard,
} from "./system/api";
import {
    isOwnWorkspacePreferenceMutation,
    isWorkspaceMediaAccessMutation,
} from "./system/api-request-guards";
import {check, logout, saveUser} from "./auth/auth-slice";
import {Sidebar as PrimeSidebar} from "primereact/sidebar";
import PageDisabled from "./system/page-disabled";
import Toolbar from "./system/components/toolbar";
import './App.css';
import {pagesMap} from './pages'
import {PrimeReactProvider} from "primereact/api";
import UnderConstruction from "./system/under-construction";
import LoadingLottie from "./system/components/loading-lottie";
import Init from "./pages/workspace/init";
import ProfileInit from "./auth/init";
import SelectWorkspaceDialog from "./pages/workspace/select-workspace-dialog";
import AcceptDenyNotificationDialog from "./pages/components/accept-deny-notification-dialog";
import useWorkspaceMembership from "./models/workspace-membership/use-workspace-membership";
import useUserEventLog from "./models/user-event-log/use-user-event-log";
import {Button} from "primereact/button";
import SelectWorkspace from "./pages/workspace/components/select-workspace";
import {RealtimeProvider} from "./system/realtime/realtime-provider";
import useWorkspaceRealtimeSync from "./system/realtime/useWorkspaceRealtimeSync";
import {translateSessionEndReason} from "./system/realtime/session-events";
import Can from "./system/can";
import {useWorkspaceRole} from "./system/use-workspace-role";
import {
    canAccessWorkspacePage,
    isWorkspaceContextIndependentPath,
    shouldBlockWorkspaceMutation,
} from "./system/workspace-permissions";
import ForcedWorkspaceSelectionDialog from "./pages/workspace/forced-workspace-selection-dialog";
import WorkspaceCreate from "./pages/workspace-create";

function ConfiguredPage({page}) {
    const {workspaceMembership, workspaceRole} = useWorkspaceRole();
    if (page.disabled) return <PageDisabled/>;

    const content = page.page
        ? React.createElement(pagesMap[page.page] || PageNotFound, {...page})
        : <Page {...page}/>;

    if (workspaceRole) {
        return canAccessWorkspacePage(workspaceRole, page) ? content : <PageDisabled/>;
    }
    if (workspaceMembership) return <PageDisabled/>;

    return (
        <Can permission={page.can} fallback={<PageDisabled/>}>
            {content}
        </Can>
    );
}

function WorkspaceRealtimeSyncBridge() {
    useWorkspaceRealtimeSync();
    return null;
}

function WorkspaceBootstrapError({error, isOnline, loading, onRetry}) {
    const translate = useTranslate();
    const backendUnavailable = error?.isNetworkError || !error?.status || Number(error.status) >= 500;
    const type = !isOnline ? "offline" : backendUnavailable ? "backend" : "error";

    return (
        <div className="w-full h-full flex align-items-center justify-content-center p-3 rtl">
            <div className="w-full md:w-8 lg:w-6 xl:w-5 surface-card shadow-2 border-round-2xl p-4 md:p-5 flex flex-column align-items-center text-center gap-3">
                <div className="w-4rem h-4rem border-circle surface-100 flex align-items-center justify-content-center">
                    <i className={`pi ${type === "offline" ? "pi-wifi" : "pi-server"} text-3xl text-primary`}/>
                </div>
                <h2 className="m-0 text-xl text-900">{translate(`workspace-bootstrap.${type}.title`)}</h2>
                <span className="text-xs text-500 line-height-3">{translate(`workspace-bootstrap.${type}.description`)}</span>
                <Button className="text-xs gap-2 mt-2" icon="pi pi-refresh" label={translate("workspace-bootstrap.retry")} loading={loading} onClick={onRetry}/>
            </div>
        </div>
    );
}

function App(props) {
    const dispatch = useDispatch();
    const {config, pages, status, homePageIndex, isDesktop, message, isDeveloper, workspaceMembershipData, workspaceSelectionRequired, notifications} = useSelector((state) => state.app);
    const {user, isAuthenticated, userChecked} = useSelector(state => state.auth);
    const authToken = getAuthToken(user);
    const authenticatedUserId = user?.documentId ?? user?.id ?? null;
    const hasAuthToken = Boolean(authToken);
    const hasAppConfig = Boolean(config);
    const [visibleSidebar, setVisibleSidebar] = useState(isDesktop);
    const [selectWorkspaceDialog, setSelectWorkspaceDialog] = useState(false);
    const [acceptDenyDialogVisible, setAcceptDenyDialogVisible] = useState(false);
    const [isOnline, setIsOnline] = useState(() => navigator.onLine);
    const workspaceMembership = useWorkspaceMembership();
    const userEventLog = useUserEventLog();
    const workspaceMembershipRef = useRef(workspaceMembership);
    const userEventLogRef = useRef(userEventLog);
    const translate = useTranslate();
    const toast = useRef();
    const selectWorkspaceRef = useRef();
    const location = useLocation();
    const navigate = useNavigate();
    const {workspaceRole} = useWorkspaceRole();
    const workspaceStatus = workspaceMembershipData?.workspace?.workspaceStatus || "active";
    const workspaceRestricted = workspaceStatus !== "active";
    const workspaceContextIndependentPath = isWorkspaceContextIndependentPath(location.pathname);
    const workspaceContentRestricted = workspaceRestricted && !workspaceContextIndependentPath;
    const roleReadOnly = shouldBlockWorkspaceMutation(workspaceRole);
    const workspaceContentRef = useRef(null);

    const invalidateSession = useCallback((reason = "session_expired") => {
        dispatch(setMessage({severity: "warn", content: translateSessionEndReason(translate, reason)}));
        dispatch(logout({remote: false}));
    }, [dispatch, translate]);

    const notifySessionCreated = useCallback(() => {
        dispatch(setMessage({
            severity: "warn",
            content: translate("security.new-session-created-toast"),
        }));
    }, [dispatch, translate]);

    workspaceMembershipRef.current = workspaceMembership;
    userEventLogRef.current = userEventLog;

    const extraPages = [];
    const hasCompletedProfile = Boolean(
        user?.confirmed
        && !user?.requiresPasswordSetup
        && String(user?.firstname || "").trim()
        && String(user?.lastname || "").trim()
        && String(user?.fullname || "").trim()
        && user?.gender
    );



    useEffect(() => {
        dispatch(fetchAppData());
    }, [dispatch]);

    useEffect(() => {
        const updateConnectionState = () => setIsOnline(navigator.onLine);
        window.addEventListener("online", updateConnectionState);
        window.addEventListener("offline", updateConnectionState);
        return () => {
            window.removeEventListener("online", updateConnectionState);
            window.removeEventListener("offline", updateConnectionState);
        };
    }, []);

    useEffect(() => {
        const element = workspaceContentRef.current;
        if (!element) return;
        if (workspaceContentRestricted) element.setAttribute("inert", "");
        else element.removeAttribute("inert");
        return () => element.removeAttribute("inert");
    }, [workspaceContentRestricted]);

    useEffect(() => {
        setWorkspaceMutationGuard((requestConfig) => {
            const ownWorkspacePreference = isOwnWorkspacePreferenceMutation(requestConfig);
            const mediaRead = isWorkspaceMediaAccessMutation(requestConfig);
            return {
                allowed: ownWorkspacePreference
                    || mediaRead
                    || (workspaceStatus === "active" && !roleReadOnly),
                message: translate("no-permission"),
            };
        });

        return () => setWorkspaceMutationGuard(null);
    }, [roleReadOnly, translate, workspaceStatus]);

    useEffect(() => {
        setApiErrorHandler((error) => {
            if (/^\/?(auth|otp|user)\//i.test(error.url)) return;
            dispatch(setMessage({severity:"error", content:error.message}));
        });
        setApiUnauthorizedHandler((error) => {
            invalidateSession(error?.details?.reason || error?.code || "session_expired");
        });

        return () => {
            setApiErrorHandler(null);
            setApiUnauthorizedHandler(null);
        };
    }, [dispatch, invalidateSession]);

    useEffect(() => {
        const handleRefresh = (event) => dispatch(saveUser(event.detail));
        window.addEventListener("platform:auth-refreshed", handleRefresh);
        return () => window.removeEventListener("platform:auth-refreshed", handleRefresh);
    }, [dispatch]);

    useEffect(()=>{
        if(config){
            const themeLink = document.getElementById("theme-css");
            if (themeLink) {
                themeLink.href = config["dark-mode"]
                    ? process.env.PUBLIC_URL + "/assets/themes/dark-theme.css"
                    : process.env.PUBLIC_URL + "/assets/themes/light-theme.css";
            }

            if (config.language==="fa"){
                document.body.classList.add("lang-fa");
            }
            else {
                document.body.classList.remove("lang-fa");
            }

            document.title = `${translate(config['app-name'])}`;
            dispatch(check());

        }
    },[config])

    useEffect(()=>{
        if (authToken){
            setHeaderToken(`Bearer ${authToken}`);
        } else {
            setHeaderToken(null);
        }
    }, [authToken])

    useEffect(()=>{
        if (authenticatedUserId && hasAuthToken && hasAppConfig) {
            workspaceMembershipRef.current.getWorkspaceMemberships();
        }
    }, [authenticatedUserId, hasAppConfig, hasAuthToken])

    useEffect(() => {
        if (authenticatedUserId && hasAuthToken && hasAppConfig) {
            userEventLogRef.current.getUserEventLogs();
            return;
        }

        dispatch(setNotifications([]));
    }, [authenticatedUserId, dispatch, hasAppConfig, hasAuthToken]);

    useEffect(() => {
        const hasUnreadDialog = isAuthenticated && notifications?.some((item) => (
            !item?.isRead && item?.displayType === "accept-deny-dialog"
        ));
        setAcceptDenyDialogVisible(Boolean(hasUnreadDialog));
    }, [isAuthenticated, notifications]);

    useEffect(()=>{
        if (message){
            const content = typeof message.content === "string"
                ? translate(message.content)
                : message.content;
            toast.current.show({life:8000, severity: message.severity, content: <span className="w-full">{content}</span>, contentClassName:"flex align-items-center"});
        }
    },[message, translate])


    const getPageConfigs=()=>{
        const _currentPageIndex = pages.findIndex((page) => page["nav-url"] && matchPath({path: page["nav-url"], end: true}, location.pathname));
        return pages[_currentPageIndex]??pages[homePageIndex];
    }

    const view = (status) =>{
        if (config&&config["under-construction"]&&!isDeveloper){
            return <UnderConstruction/>
        }
        else {
            switch (status) {
                case 'loading':return (
                    <LoadingLottie/>
                );
                case 'succeeded':
                    return (
                        <div className={`${config.dir} ${config.language==="fa"&&"lang-fa"} flex flex-column h-full w-full `}>
                            <Toast className="pr-3" ref={toast} position="bottom-left"/>
                            <RealtimeProvider
                                token={authToken}
                                currentSessionId={user?.session?.id}
                                workspaceId={workspaceMembershipData?.workspace?.id}
                                workspaceDocumentId={workspaceMembershipData?.workspace?.documentId}
                                onSessionCreated={notifySessionCreated}
                                onSessionInvalidated={invalidateSession}
                            >
                            <WorkspaceRealtimeSyncBridge/>
                            {config.authorization?(isAuthenticated?(
                                <div  className="w-full h-full flex flex-1 align-items-center justify-content-center">
                                    {
                                        hasCompletedProfile?(
                                            <>
                                                {workspaceMembership.status!=="succeeded"?(
                                                    workspaceMembership.status === "failed" ? (
                                                        <WorkspaceBootstrapError error={workspaceMembership.error} isOnline={isOnline} loading={workspaceMembership.loading} onRetry={workspaceMembership.getWorkspaceMemberships}/>
                                                    ) : (
                                                        <LoadingLottie/>
                                                    )
                                                ):workspaceMembershipData==null?(
                                                    workspaceSelectionRequired && location.pathname === "/workspace/create"
                                                        ? <WorkspaceCreate/>
                                                        : <Init/>
                                                ):(
                                                    <div className="w-full h-full flex flex-1 align-items-center">
                                                        {(config.sidebar) && (
                                                            <div className={`h-full ${!getPageConfigs()?.sidebar && "p-hidden"}`}>
                                                                {isDesktop ? (<Sidebar extraPages={extraPages} expanded={visibleSidebar}/>):(
                                                                    <div className="flex lg:hidden shadow-2">
                                                                        <PrimeSidebar className="w-fit" dir={config.dir} showCloseIcon={false} position={`${config.dir==="rtl"?"right":"left"}`} visible={visibleSidebar} onHide={()=>{setVisibleSidebar(false)}}>
                                                                            <Sidebar expanded={true} onChange={(e)=>{setVisibleSidebar(false)}}/>
                                                                        </PrimeSidebar>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div className="h-full flex-1 flex flex-column overflow-hidden">
                                                            {getPageConfigs()?.toolbar&&<Toolbar readOnly={workspaceRestricted || roleReadOnly} onTitleClicked={(e)=>{ selectWorkspaceRef.current.toggle(e);{/*navigate(`/workspace`)*/}}}  setVisibleSidebar={setVisibleSidebar} visibleSidebar={visibleSidebar}/>}
                                                            {workspaceRestricted && (
                                                                <div className="w-full flex flex-column sm:flex-row align-items-stretch sm:align-items-center gap-2 px-3 py-2 border-bottom-1 border-200 surface-50">
                                                                    <Message className="flex-1 justify-content-start text-xs" severity="warn" text={translate(`workspace-status.${workspaceStatus}`)}/>
                                                                </div>
                                                            )}
                                                            <SelectWorkspace ref={selectWorkspaceRef} onChange={()=>{navigate(`/`)}}/>

                                                            <div className="flex-1 w-full overflow-auto relative">
                                                                <div
                                                                    ref={workspaceContentRef}
                                                                    className={`h-full w-full transition-duration-200 ${workspaceContentRestricted ? "pointer-events-none select-none opacity-50" : ""}`}
                                                                    aria-disabled={workspaceContentRestricted}
                                                                >
                                                                <Routes>
                                                                    <Route path="/" element={<ConfiguredPage page={pages[homePageIndex]}/>}/>
                                                                    {
                                                                        pages.map(e=>(
                                                                            <Route key={`${e.name}-${e['nav-url']}`} path={e['nav-url']} element={<ConfiguredPage page={e}/>}/>
                                                                        ))
                                                                    }
                                                                    <Route path="*" element={<PageNotFound/>}/>
                                                                </Routes>
                                                                </div>
                                                                {workspaceContentRestricted && (
                                                                    <div className="absolute top-0 left-0 w-full h-full z-5 flex align-items-center justify-content-center p-3">
                                                                        <div className="surface-card border-1 border-200 border-round-xl shadow-3 p-4 flex flex-column align-items-center text-center gap-3 max-w-30rem">
                                                                            <i className="pi pi-lock text-4xl text-orange-500"/>
                                                                            <span className="font-bold text-base">{translate("workspace-status.readonly-title")}</span>
                                                                            <span className="text-xs text-600 line-height-3">{translate(`workspace-status.${workspaceStatus}`)}</span>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <SelectWorkspaceDialog visible={selectWorkspaceDialog} setVisible={setSelectWorkspaceDialog}/>
                                                            {/*<div className="flex-1 h-full flex overflow-hidden">*/}

                                                            {/*</div>*/}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ):(
                                            <div className="w-full h-full flex flex-1 align-items-center justify-content-center">
                                                <ProfileInit/>
                                            </div>
                                        )
                                    }
                                </div>
                            ):<Auth/>):(
                                <>
                                    {config.sidebar && (<Sidebar/>)}
                                    <Routes>
                                        {/*{pages.length>0&&}<Route path="/" element={<Page {...pages[0]}/>}/>*/}
                                        {
                                            pages.map(e=><Route path={e['nav-url']} element={<Page {...e}/>}/>)
                                        }
                                        <Route path="*" element={<PageNotFound/>}/>
                                    </Routes>
                                </>
                            )}
                            </RealtimeProvider>
                        </div>
                    );
                case 'failed':return (
                    <div>Error</div>
                )
                default: return(<div>{status}</div>)
            }
        }


    }

    return (

        <PrimeReactProvider>
            <div className="w-screen h-screen overflow-hidden surface-ground flex align-items-center justify-content-center">
                {userChecked?view(status):view('loading')}
                {config&&<AcceptDenyNotificationDialog visible={acceptDenyDialogVisible} setVisible={setAcceptDenyDialogVisible}/>}
                {config && isAuthenticated && location.pathname !== "/workspace/create" && <ForcedWorkspaceSelectionDialog/>}

            </div>
        </PrimeReactProvider>

    );
}

export default App;
